import React, { useEffect, useMemo, useRef } from "react";
import { useMarketing } from "./context";
import { MARKETING_PLACEMENTS } from "./normalizer";

const renderImage = (asset, className) => (
  <picture className={`${className}-picture`}>
    {asset.desktopSrc ? (
      <source media="(min-width: 768px)" srcSet={asset.desktopSrc} />
    ) : null}
    <img src={asset.src} alt={asset.alt} loading="lazy" />
  </picture>
);

export default function MarketingBannerSlot({
  placement,
  index = null,
  maxItems = 1,
  className = "",
}) {
  const slotRef = useRef(null);
  const {
    enabled,
    getBannersForPlacement,
    recordBannerImpression,
    dismissBanner,
    handleCreativeClick,
    notifySlotVisible,
  } = useMarketing();

  const banners = useMemo(
    () => getBannersForPlacement(placement, index).slice(0, maxItems),
    [getBannersForPlacement, index, maxItems, placement]
  );

  const bannerKeys = banners.map((item) => item.storageKey).join("|");

  useEffect(() => {
    if (!enabled || banners.length === 0 || !slotRef.current) return undefined;

    const markVisible = () => {
      notifySlotVisible(placement);
      banners.forEach((item) => recordBannerImpression(item, placement));
    };

    if (typeof window === "undefined" || !window.IntersectionObserver) {
      const timer = setTimeout(markVisible, 0);
      return () => clearTimeout(timer);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          markVisible();
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(slotRef.current);

    return () => observer.disconnect();
  }, [
    bannerKeys,
    banners,
    enabled,
    notifySlotVisible,
    placement,
    recordBannerImpression,
  ]);

  if (!enabled || banners.length === 0) return null;

  const floatingOffset = banners.find(
    (item) => typeof item.creative.floatingOffsetPx === "number"
  )?.creative.floatingOffsetPx;
  const headerBottomOffset = banners.find(
    (item) => typeof item.creative.headerBottomOffsetPx === "number"
  )?.creative.headerBottomOffsetPx;
  const slotStyle = {
    ...(placement === MARKETING_PLACEMENTS.FLOATING_ABOVE_PLAYER &&
    floatingOffset !== undefined
      ? { "--marketing-floating-offset": `${floatingOffset}px` }
      : {}),
    ...(placement === MARKETING_PLACEMENTS.HEADER_TOP &&
    headerBottomOffset !== undefined
      ? { "--marketing-header-bottom-offset": `${headerBottomOffset}px` }
      : {}),
  };

  return (
    <section
      ref={slotRef}
      className={[
        "marketing-banner-slot",
        `marketing-banner-slot--${placement}`,
        className,
      ].join(" ")}
      data-marketing-placement={placement}
      style={Object.keys(slotStyle).length > 0 ? slotStyle : undefined}
    >
      {banners.map((item) => {
        const { creative } = item;
        const hasTarget = !!creative.target?.href;
        const CardTag = hasTarget ? "button" : "div";

        return (
          <article className="marketing-banner" key={item.storageKey}>
            <CardTag
              type={hasTarget ? "button" : undefined}
              className={[
                "marketing-banner-card",
                hasTarget ? "marketing-banner-card--clickable" : "",
              ].join(" ")}
              onClick={hasTarget ? () => handleCreativeClick(item) : undefined}
              aria-label={creative.asset.alt || creative.title || "Marketing"}
            >
              {renderImage(creative.asset, "marketing-banner")}
              {(creative.title || creative.body || creative.ctaLabel) && (
                <span className="marketing-banner-copy">
                  {creative.title ? (
                    <strong className="marketing-banner-title">
                      {creative.title}
                    </strong>
                  ) : null}
                  {creative.body ? (
                    <span className="marketing-banner-body">{creative.body}</span>
                  ) : null}
                  {creative.ctaLabel && hasTarget ? (
                    <span className="marketing-banner-cta">
                      {creative.ctaLabel}
                    </span>
                  ) : null}
                </span>
              )}
            </CardTag>
            {creative.dismissible ? (
              <button
                type="button"
                className="marketing-close-button marketing-banner-close"
                aria-label="Cerrar anuncio"
                onClick={(event) => {
                  event.stopPropagation();
                  dismissBanner(item);
                }}
              >
                &times;
              </button>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}
